import React, { FC, useState, useEffect } from "react";
import validator from "@rjsf/validator-ajv6";
import { withTheme } from "@rjsf/core";
import { Theme } from "@rjsf/antd";
import { message, Select, Switch, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { Loading } from "../../components/loading";
import { Version } from "../../types/version";
import $authHost from "../../http/auth";
import styles from "./applications.module.css";
import { useSelector } from "react-redux";
import { getUser } from "../selectors";
import permissions from "../../constants/permissions";
import { replaceUndefinedWithEmptyString } from "../../utils/replaceUndefinedWithEmptyString";

const { Option } = Select;

// @ts-ignore
const Form = withTheme(Theme);

interface Props {
  applicationId: number | string | null;
  onApplicationSave: Function;
}

export const ApplicationForm: FC<Props> = ({ applicationId, onApplicationSave }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [applicationName, setApplicationName] = useState<string>("");
  const [applicationDescription, setApplicationDescription] = useState<string>("");
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);
  const [version, setVersion] = useState<Version>();
  const [versions, setVersions] = useState<Version[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<null | string>(null);
  const user = useSelector(getUser);
  const { t } = useTranslation("applications");

  const userCanReadConfiguration = user.permissions.includes(permissions.MANAGE_CONFIGURATIONS_READ);
  const userCanWriteConfiguration = user.permissions.includes(permissions.MANAGE_CONFIGURATIONS_WRITE);

  useEffect(() => {
    if (typeof applicationId !== "number") {
      return;
    }

    Promise.all([
      $authHost.get(`/Configurations/${user.role}/application/${applicationId}/versions`),
      $authHost.get(`/Applications/${applicationId}`),
    ])
      .then(([versionsRes, applicationRes]) => {
        setVersions(versionsRes.data);
        setSelectedVersionId(applicationRes.data.versionId);
        setApplicationName(applicationRes.data.name);
        setApplicationDescription(applicationRes.data.description);
        setIsEnabled(applicationRes.data.isEnabled);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  }, [applicationId, user]);

  useEffect(() => {
    if (!versions.length || selectedVersionId === null) {
      return;
    }

    const version = versions.find((version) => version.versionId === selectedVersionId);

    if (version) {
      setVersion(version);
    }
  }, [selectedVersionId, versions]);

  const onSubmit = (configuration: any) => {
    setIsSubmitting(true);

    $authHost
      .put(`/Applications/${applicationId}`, {
        configuration: replaceUndefinedWithEmptyString(configuration),
        schema: version!.schema,
        isEnabled,
        name: applicationName,
        description: applicationDescription,
        versionId: selectedVersionId,
      })
      .then((res) => {
        message.success(t("applicationSaved"));
        onApplicationSave(res.data);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <div className={styles.applicationFormHeader}>
        <p className={styles.font16}>
          {t("application")}: <strong>{applicationName}</strong>
        </p>
        <Select
          placeholder={t("selectVersion")}
          value={selectedVersionId}
          onChange={(value) => setSelectedVersionId(value)}
        >
          {versions.map((version) => (
            <Option key={version.versionId} value={version.versionId}>
              {version.number}
            </Option>
          ))}
        </Select>
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {isSubmitting && <Loading />}
          {version && (
            <>
              <div className={styles.applicationFormItem}>
                <p className={styles.applicationFormLabel}>{t("enabled")}</p>
                <Tooltip title={t(isEnabled ? "disable" : "enable")}>
                  <Switch
                    defaultChecked={isEnabled ?? undefined}
                    onClick={(val: boolean) => {
                      setIsEnabled(val);
                    }}
                  />
                </Tooltip>
              </div>
              {userCanReadConfiguration && (
                <Form
                  className="json-form"
                  schema={{ ...version.schema, $id: "http://json-schema.org/draft-04/schema#" }}
                  formData={version.configuration}
                  readonly={!userCanWriteConfiguration}
                  uiSchema={{
                    "ui:options": {
                      expandable: false,
                    },
                  }}
                  validator={validator}
                  onChange={(value: any) => {
                    console.log("changed", value);
                  }}
                  onSubmit={(value: any) => {
                    onSubmit(value.formData);
                  }}
                  onError={(value: any) => {
                    console.log("errors", value);
                  }}
                ></Form>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
